import { useNavigate } from 'react-router-dom';
import { Sparkles, Video as VideoIcon, ShieldOff, Server, Chrome, ArrowRight } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';

const SAMPLE_PLATFORMS = [
  'YouTube',
  'TikTok',
  'Instagram',
  'X / Twitter',
  'Vimeo',
  'Reddit',
  'Twitch clips',
  'SoundCloud',
];

export function MediaDownloader() {
  const navigate = useNavigate();
  return (
    <div>
      <ToolHeader
        toolId="media-downloader"
        title="Media downloader"
        description="Save audio or video from YouTube, TikTok, Instagram, X, and more."
        badges={<Badge variant="warning">coming soon</Badge>}
      />

      <Card className="!p-8 mb-4 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 size-56 rounded-full bg-amber-300/40 dark:bg-amber-500/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 size-56 rounded-full bg-orange-300/30 dark:bg-orange-500/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-2xl">
          <div className="size-14 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 border-2 border-fg/85 dark:border-fg/40 shadow-sticker flex items-center justify-center rotate-[-3deg] mb-5">
            <Sparkles size={22} className="text-white drop-shadow-[0_1px_0_rgba(0,0,0,0.3)]" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight">A great browser-only version is hard.</h2>
          <p className="text-sm text-fg-muted mt-3 leading-relaxed">
            We tried to ship YouTube → MP3 directly here, but doing it without a backend is genuinely not
            workable right now. YouTube actively blocks browser-only audio extraction, the official cobalt
            API now requires JWT authentication, and community instances are unstable. We'd rather not
            ship a button that fails 70% of the time.
          </p>
          <p className="text-sm text-fg-muted mt-3 leading-relaxed">
            We're keeping this slot warm and exploring two paths: a self-hosted cobalt connector you can
            point at your own instance, and a browser-extension companion that handles the parts a regular
            web tool can't. Either way, when it ships it'll work reliably.
          </p>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card>
          <div className="flex items-start gap-3">
            <div className="size-10 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent shrink-0">
              <VideoIcon size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold">Need it now? Use the screen recorder.</h3>
              <p className="text-xs text-fg-muted mt-1 leading-relaxed">
                Open the source in another tab, share that tab to the Screen recorder with audio enabled,
                hit Record, and play through. You'll get a clean WebM file with the audio track. It's
                real-time, but it works.
              </p>
              <Button
                size="sm"
                variant="secondary"
                className="mt-3"
                onClick={() => navigate('/tool/screen-recorder')}
              >
                Open Screen recorder
                <ArrowRight size={11} />
              </Button>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-start gap-3">
            <div className="size-10 rounded-xl bg-success/10 border border-success/20 flex items-center justify-center text-success shrink-0">
              <Server size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold">Self-host cobalt today.</h3>
              <p className="text-xs text-fg-muted mt-1 leading-relaxed">
                One <code className="font-mono px-1 py-0.5 rounded bg-bg-inset border border-border">docker compose up -d</code> spins up your
                own cobalt instance with no rate limits or auth. We'll add a connector here that points at
                your URL.
              </p>
              <Button
                size="sm"
                variant="secondary"
                className="mt-3"
                onClick={() =>
                  window.open(
                    'https://github.com/imputnet/cobalt/blob/main/docs/run-an-instance.md',
                    '_blank',
                  )
                }
              >
                Self-host guide
                <ArrowRight size={11} />
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <Card>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <ShieldOff size={14} className="text-fg-subtle" />
          Why no backend-free version?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs text-fg-muted leading-relaxed">
          <div>
            <strong className="text-fg block mb-0.5">Cipher decryption</strong>
            YouTube ships obfuscated signatures that change every few weeks. <code className="font-mono">ytdl-core</code>-style libraries
            break constantly and need server-side updates.
          </div>
          <div>
            <strong className="text-fg block mb-0.5">CORS lockdown</strong>
            Even if you decrypt the URL, YouTube's audio CDNs reject browser fetches via CORS. Only
            their player or a server with the right headers can pull the bytes.
          </div>
          <div>
            <strong className="text-fg block mb-0.5">Embedded player</strong>
            The IFrame API plays video but locks down the audio MediaStream — cross-origin policy
            prevents capturing the track.
          </div>
          <div>
            <strong className="text-fg block mb-0.5">Public proxies</strong>
            CORS proxies and free downloader APIs get rate-limited or DMCAed within weeks. Not a
            foundation we'd build on.
          </div>
        </div>
      </Card>

      <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex flex-wrap gap-1.5">
          {SAMPLE_PLATFORMS.map((p) => (
            <span
              key={p}
              className="px-2.5 py-1 text-2xs rounded-md bg-bg-inset border border-border font-medium text-fg-subtle"
            >
              {p}
            </span>
          ))}
        </div>
        <div className="text-2xs text-fg-subtle inline-flex items-center gap-1.5">
          <Chrome size={12} />
          Browser-extension companion in scoping
        </div>
      </div>
    </div>
  );
}

export default MediaDownloader;
